import React from 'react';
import { observer } from 'mobx-react';
import { app_settings } from '../SettingsController';
import { settingsController } from '../SettingsController';

// Pure helpers, exported for tests.

// 0-based monitor id from the WinForms DeviceName "DISPLAYn". Same number the
// Identify overlay flashes on each screen. Match the digits explicitly:
// Number("") is 0 and Number("foo") is NaN (neither throws), so split-and-parse
// could leak a bad id into adjacency, React keys, and the .rdp.
export const monitorId = (monitor, i) => {
  const m = /DISPLAY(\d+)/.exec((monitor && monitor.DeviceName) || '');
  return m ? Number(m[1]) - 1 : i;
};

// Normalize a WinForms monitor to a plain rectangle in virtual-desktop pixels.
export const getBounds = (monitor) => {
  const b = (monitor && monitor.Bounds) || {};
  const x = Number(b.X) || 0;
  const y = Number(b.Y) || 0;
  const w = Number(b.Width) || 0;
  const h = Number(b.Height) || 0;
  return { x, y, w, h, right: x + w, bottom: y + h };
};

// Adjacent means the two rectangles share an overlapping edge, not just a
// touching corner. Based on geometry, not DISPLAY numbering.
export const areAdjacent = (a, b) => {
  const A = getBounds(a);
  const B = getBounds(b);
  const xOverlap = Math.min(A.right, B.right) - Math.max(A.x, B.x);
  const yOverlap = Math.min(A.bottom, B.bottom) - Math.max(A.y, B.y);
  const sharesVerticalEdge = A.right === B.x || B.right === A.x;
  const sharesHorizontalEdge = A.bottom === B.y || B.bottom === A.y;
  return (sharesVerticalEdge && yOverlap > 0) || (sharesHorizontalEdge && xOverlap > 0);
};

// Adjacency graph keyed by monitor id: { id: Set(neighbourId) }.
export const buildAdjacency = (monitors) => {
  const ids = monitors.map(monitorId);
  const adj = {};
  ids.forEach((id) => { adj[id] = new Set(); });
  for (let i = 0; i < monitors.length; i++) {
    for (let j = i + 1; j < monitors.length; j++) {
      if (areAdjacent(monitors[i], monitors[j])) {
        adj[ids[i]].add(ids[j]);
        adj[ids[j]].add(ids[i]);
      }
    }
  }
  return adj;
};

// True when the selected ids form one gap-free region (every id reachable
// from the first through selected neighbours).
export const isConnected = (ids, adj) => {
  if (ids.length <= 1) return true;
  const wanted = new Set(ids);
  const seen = new Set([ids[0]]);
  const stack = [ids[0]];
  while (stack.length) {
    const cur = stack.pop();
    const neighbours = adj[cur] ? adj[cur] : new Set();
    neighbours.forEach((n) => {
      if (wanted.has(n) && !seen.has(n)) {
        seen.add(n);
        stack.push(n);
      }
    });
  }
  return seen.size === wanted.size;
};

// Can add a monitor only if it touches the current selection, or nothing is
// selected yet. Keeps the region contiguous as it grows.
export const canSelect = (id, selected, adj) =>
  !selected.includes(id) &&
  (selected.length === 0 || selected.some((s) => adj[id] && adj[id].has(s)));

// Can remove a monitor only if the rest stay connected, and never the last
// one. Blocks dropping a bridge screen that would split the selection.
export const canDeselect = (id, selected, adj) => {
  if (!selected.includes(id) || selected.length <= 1) return false;
  return isConnected(selected.filter((x) => x !== id), adj);
};

// Why a tile can't be toggled, or '' if it can. One source for both the click
// handler and the render, so they can't drift.
//   'blocked' — not selected, not touching the selection
//   'locked'  — selected, dropping it would split the region
//   'last'    — selected, and the only one left
export const blockReason = (id, selected, adj) => {
  if (!selected.includes(id)) return canSelect(id, selected, adj) ? '' : 'blocked';
  if (selected.length === 1) return 'last';
  return canDeselect(id, selected, adj) ? '' : 'locked';
};

// Spoken explanation per reason, appended to the tile's accessible label.
const REASON_LABEL = {
  blocked: ', not selectable, add a display next to your selection first',
  locked: ', selected, cannot be removed without leaving a gap',
  last: ', selected, at least one display must stay selected',
};

// Clean a persisted selection against the monitors present now: drop unknown
// ids, keep the rest if still contiguous, otherwise fall back to all.
export const repairSelection = (selected, monitors) => {
  const ids = monitors.map(monitorId);
  const adj = buildAdjacency(monitors);
  const known = (selected || []).filter((x) => ids.includes(x));
  if (known.length > 0 && isConnected(known, adj)) return [...known];
  return [...ids];
};

// Scale the monitor rectangles into a canvas box, preserving their real
// positions.
const CANVAS_MAX_W = 300;
const CANVAS_MAX_H = 168;
const CANVAS_PAD = 6;
const TILE_GAP = 2;

export const layoutTiles = (monitors) => {
  const rects = monitors.map(getBounds);
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.right));
  const maxY = Math.max(...rects.map((r) => r.bottom));
  const spanX = (maxX - minX) || 1;
  const spanY = (maxY - minY) || 1;
  const scale = Math.min(
    (CANVAS_MAX_W - 2 * CANVAS_PAD) / spanX,
    (CANVAS_MAX_H - 2 * CANVAS_PAD) / spanY
  );
  const canvas = {
    width: spanX * scale + 2 * CANVAS_PAD,
    height: spanY * scale + 2 * CANVAS_PAD,
  };
  const tiles = monitors.map((monitor, i) => {
    const r = rects[i];
    return {
      id: monitorId(monitor, i),
      primary: !!monitor.Primary,
      realWidth: r.w,
      realHeight: r.h,
      left: CANVAS_PAD + (r.x - minX) * scale + TILE_GAP / 2,
      top: CANVAS_PAD + (r.y - minY) * scale + TILE_GAP / 2,
      width: Math.max(0, r.w * scale - TILE_GAP),
      height: Math.max(0, r.h * scale - TILE_GAP),
    };
  });
  return { canvas, tiles };
};

class MultimonSectionInner extends React.Component {
  state = { monitors: undefined, loading: false, nudgeId: null };

  componentDidMount() {
    this._mounted = true;
    if (app_settings.get().useMultimonSelection) this.loadMonitors(false);
    // The settings modal stays mounted, so re-query on each open to catch
    // displays that were plugged in or rearranged since last time.
    if (window.$) {
      this._onModalShow = () => {
        if (app_settings.get().useMultimonSelection) this.loadMonitors(true);
      };
      window.$('#settings-modal').on('show.bs.modal', this._onModalShow);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    clearTimeout(this._nudgeTimer);
    if (window.$ && this._onModalShow) {
      window.$('#settings-modal').off('show.bs.modal', this._onModalShow);
    }
  }

  // Brief shake on a tile the user cannot toggle, so a blocked or locked
  // click reads as "not allowed" rather than "unresponsive".
  nudge(id) {
    this.setState({ nudgeId: id });
    clearTimeout(this._nudgeTimer);
    this._nudgeTimer = setTimeout(() => this.setState({ nudgeId: null }), 320);
  }

  // Query the OS for the current monitors. Lazy by default so a plain launch
  // never spawns powershell.exe; force=true re-queries after a display change.
  // _loading guards overlapping calls, since state.loading lags (setState is async).
  async loadMonitors(force) {
    if (!window.api.isWindowsOS || this._loading) return;
    if (!force && this.state.monitors !== undefined) return;
    this._loading = true;
    this.setState({ loading: true });
    let monitors = [];
    try {
      monitors = (await window.api.getWindowsMonitors()) || [];
    } catch (e) {
      monitors = []; // fall through to the "couldn't detect" state
    } finally {
      this._loading = false;
    }
    if (!this._mounted) return;

    // Empty means the query failed — Windows always reports >=1 display.
    // Repairing against [] drops the selection to "all" and persists it, so a
    // flaky powershell call must not touch settings. Persist only on a real list.
    if (monitors.length > 0) {
      const settings = app_settings.get();
      const repaired = repairSelection(settings.multimonSelected, monitors);
      if (JSON.stringify(repaired) !== JSON.stringify(settings.multimonSelected)) {
        app_settings.set({ ...settings, multimonSelected: repaired });
      }
      this.setState({ monitors, loading: false });
      return;
    }

    // Nothing came back: leave settings alone. Hold the last-good map if we
    // have one; only show the empty state on a cold first scan.
    this.setState((prev) => ({
      loading: false,
      monitors: prev.monitors && prev.monitors.length > 0 ? prev.monitors : [],
    }));
  }

  setMode(choose) {
    settingsController.setOptionByValue('useMultimonSelection', choose);
    if (choose) this.loadMonitors(false);
  }

  toggleMonitor(id) {
    const settings = app_settings.get();
    const selected = settings.multimonSelected || [];
    const adj = buildAdjacency(this.state.monitors || []);
    if (blockReason(id, selected, adj)) return this.nudge(id);
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    app_settings.set({ ...settings, multimonSelected: next });
  }

  render() {
    if (!window.api.isWindowsOS) return null;
    const settings = app_settings.get();
    const choose = !!settings.useMultimonSelection;
    const monitors = this.state.monitors;

    return (
      <div className="mm-section">
        <div className="mm-mode">
          <span className="mm-label">Displays</span>
          {choose && monitors && monitors.length > 0 &&
            <button type="button" className="ident-btn"
              onClick={() => window.api.identifyScreens && window.api.identifyScreens(monitors)}>
              <i className="fa fa-desktop" aria-hidden="true"></i> Identify
            </button>}
          <div className="seg2" role="group" aria-label="Display selection mode">
            <button type="button" aria-pressed={!choose}
              className={!choose ? 'on' : ''} onClick={() => this.setMode(false)}>All displays</button>
            <button type="button" aria-pressed={choose}
              className={choose ? 'on' : ''} onClick={() => this.setMode(true)}>
              Choose <span className="badge-beta">Beta</span>
            </button>
          </div>
        </div>

        {/* Spinner only on the first load. A refresh on reopen keeps the
            current map visible so it doesn't flash out. */}
        {choose && this.state.loading && (!monitors || monitors.length === 0) &&
          <div className="mm-loading" role="status" aria-label="Detecting displays">
            <i className="fa fa-spinner fa-pulse" aria-hidden="true"></i>
          </div>}

        {choose && monitors && monitors.length > 0 &&
          this.renderPicker(monitors, settings.multimonSelected || [])}

        {choose && !this.state.loading && monitors && monitors.length === 0 &&
          <p className="mm-note mm-empty" role="status">
            Couldn't detect your displays. Try reopening settings.
          </p>}
      </div>
    );
  }

  renderPicker(monitors, selected) {
    const adj = buildAdjacency(monitors);
    const { canvas, tiles } = layoutTiles(monitors);

    return (
      <div className="mm-pick">
        <div className="mon-map" style={{ width: canvas.width, height: canvas.height }}>
          {tiles.map((tile) => {
            const isOn = selected.includes(tile.id);
            const why = blockReason(tile.id, selected, adj);
            const held = why === 'locked' || why === 'last';  // selected, undroppable
            // Don't mark 'last' disabled: a lone selected display isn't a broken
            // control (on a single-monitor machine it's the only tile). Its
            // label carries the rule instead.
            const unavailable = why === 'blocked' || why === 'locked';
            // Drop the resolution line on tiles too small to hold it; it stays
            // in the accessible label either way.
            const showDim = tile.width >= 54 && tile.height >= 38;
            const cls = 'mon-cell'
              + (isOn ? ' on' : '')
              + (held ? ' locked' : '')
              + (why === 'blocked' ? ' blocked' : '')
              + (this.state.nudgeId === tile.id ? ' nudge' : '');
            const label = `Display ${tile.id + 1}`
              + (tile.primary ? ', primary' : '')
              + `, ${tile.realWidth} by ${tile.realHeight}${REASON_LABEL[why] || ''}`;
            return (
              <button type="button" key={tile.id} className={cls}
                aria-label={label} aria-pressed={isOn} aria-disabled={unavailable}
                style={{ left: tile.left, top: tile.top, width: tile.width, height: tile.height }}
                onClick={() => this.toggleMonitor(tile.id)}>
                {tile.primary && <i className="fa fa-star mon-star" aria-hidden="true"></i>}
                <b>{tile.id + 1}</b>
                {showDim && <span>{tile.realWidth}×{tile.realHeight}</span>}
              </button>
            );
          })}
        </div>
        <div className="mm-foot">
          <span className="mm-count" role="status">{selected.length} of {monitors.length} selected</span>
          <span className="mm-note">Displays must sit next to each other, with no gaps.</span>
        </div>
      </div>
    );
  }
}

export const MultimonSection = observer(MultimonSectionInner);
export default MultimonSection;
